import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { maintenanceDuration, maintenanceStartTime } from "src/constants/general-notification";

function MaintenanceGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  const isOnMaintenancePage = location.pathname === "/maintenance";

  const isMaintenanceActive = () => {
    if (
      !maintenanceStartTime ||
      Number.isNaN(maintenanceStartTime?.getTime()) ||
      maintenanceDuration <= 0
    ) {
      return false;
    }
  
    const now = Date.now();
    const start = maintenanceStartTime.getTime();
    const end = start + maintenanceDuration * 60 * 60 * 1000;
  
    return now >= start && now <= end;
  };

  const [isActive, setIsActive] = useState(() => isMaintenanceActive());

  const startTimeoutRef = useRef<number | null>(null);
  const endTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const current = isMaintenanceActive();

    setIsActive(prev => {
      if (prev !== current) {
        return current;
      }
      return prev;
    });
  }, [location.pathname]);

  // Time-based maintenance page redirection
  useEffect(() => {
    if (!maintenanceStartTime || maintenanceDuration <= 0) return;

    const now = Date.now();
    const start = maintenanceStartTime.getTime();
    const end = start + maintenanceDuration * 60 * 60 * 1000;

    if (startTimeoutRef.current) clearTimeout(startTimeoutRef.current);
    if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);

    // Schedule maintenance start
    if (now < start) {
      startTimeoutRef.current = window.setTimeout(() => {
        setIsActive(true);
      }, start - now);
    }

    // Schedule maintenance end
    if (now < end) {
      endTimeoutRef.current = window.setTimeout(() => {
        setIsActive(false);
      }, end - now);
    }

    return () => {
      if (startTimeoutRef.current) clearTimeout(startTimeoutRef.current);
      if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isActive && !isOnMaintenancePage) {
      navigate("/maintenance", { replace: true });
    }

    if (!isActive && isOnMaintenancePage) {
      navigate("/", { replace: true });
    }
  }, [isActive, isOnMaintenancePage, navigate]);

  return <Outlet />;
};

export default MaintenanceGuard;
